/*
 * Set-Builder Engine (Uplifts custom)
 *
 * Drives the <set-builder> block rendered by snippets/set-builder.liquid: a Doonails-style
 * set selection on the PDP (single / 3-piece / 6-piece, color slots filled via drawer).
 * Multi-size mode ("sources"): the drawer stacks one color group per size product (with a
 * subheading each), any slot holds any size + color, and the button price is summed live
 * from the picked variants' prices.
 * Optional surprise gift: from a configurable number of items the gift card un-greys and its
 * variant is appended to the same add call (one extra line item), tagged with the
 * `_set_gift` line-item property. Not priced here — an automatic discount in the admin makes
 * it free. One gift per built set: two sets add two gifts, which Shopify merges into one line
 * of quantity 2 (same variant, same properties). That is intended — the gift scales with the
 * sets, it is only the manual quantity stepper that is taken away in the cart.
 *
 * Drawer = multi-select session with a DRAFT: while open, stepper clicks edit a scratch copy
 * of the selection (counts per variant + a scratch tier size); the sticky footer previews it
 * (thumbnails + running count) and "Apply selection" commits draft -> slots in one go. Closing
 * without applying discards the draft (it is rebuilt from the slots on every open). Exceeding
 * the scratch size upgrades to the next bigger tier automatically — never downwards. Above the
 * biggest tier the set keeps growing one piece at a time: the tiers are an INCENTIVE, not a
 * cap, so piece 7, 8, ... are picked like any other and ride along into the cart. The PDP slot
 * grid is cloned out to match (see ensureSlotCapacity). Applying an incomplete selection is
 * allowed: the ATC button stays gated and REOPENS the drawer instead of submitting.
 *
 * Cart contract ("single lines"): the chosen color variants are posted to /cart/add.js as
 * REGULAR line items in one call. Quantity incentives (free shipping >= 3, discount >= 6)
 * are native automatic discounts configured in the Shopify admin — nothing is priced here.
 * The payload builder is isolated in buildAddPayload() so a future bundle-app backend only
 * has to swap that one function.
 *
 * Integration notes:
 * - The add flow replicates ProductForm._onSubmit (assets/theme.js) event for event
 *   (cart:prepare-bundled-sections -> POST -> variant:add + cart:change / cart:error), so the
 *   cart drawer refreshes and opens exactly like a native add.
 * - Add-to-cart buttons are intercepted at CLICK level in the capture phase via their
 *   form association — this covers the in-form button, the sticky-bar buttons and Enter.
 * - All listeners are document-level delegations behind a once-guard: the drawer node is
 *   re-parented to <body> on open, and the theme editor replaces section HTML.
 */

const CONTRACT = 'single_lines';

/*
 * Marks the gift line in the cart. Mirrors the cart-gifts engine's convention (underscore =
 * hidden from the customer at checkout), but deliberately uses its OWN key: `_delete_under`
 * would hand the line to cart-gifts.js's cart-value-based autoDeletePass, and the set gift
 * hangs off the set size instead. snippets/line-item.liquid reads this to drop the quantity
 * selector — the gift is not a line the shopper may count up by hand.
 */
const GIFT_PROPERTY_KEY = '_set_gift';

const registry = new Map(); // form id -> SetBuilder instance

class SetBuilder extends HTMLElement {
  connectedCallback() {
    const configEl = this.querySelector('[data-set-builder-config]');
    if (!configEl) return;
    const config = JSON.parse(configEl.textContent);
    this.tiers = config.tiers;
    this.variants = config.variants;
    this.strings = config.strings;
    this.sources = config.sources || [];
    this.pricing = config.pricing || {};
    // Optional surprise gift: unlocks from `threshold` items and rides along in the same
    // /cart/add.js call. Pricing is not our business — an admin discount makes it free.
    this.gift = config.gift || { enabled: false };
    this.giftEl = this.querySelector('[data-set-gift]');
    // Multi-size mode: slots mix variants from several size products; prices differ, so the
    // button total is summed live (see setPriceHtml) and slot labels carry the size label.
    this.multiSource = Boolean(this.pricing.multiSource) && this.sources.length > 1;
    this.formId = this.getAttribute('form');
    this.drawerId = this.getAttribute('drawer');
    this.slotEls = Array.from(this.querySelectorAll('[data-slot]'));
    this.size = this.tiers.defaultSize || 1;
    // Enabled tier sizes ascending, for the drawer's automatic upgrade to the next bigger set.
    this.tierSizes = Object.keys(this.tiers)
      .filter((key) => key !== 'defaultSize')
      .map(Number)
      .sort((a, b) => a - b);
    this.slots = new Array(this.size).fill(null);
    // Drawer draft: counts per variant id + a scratch tier size, rebuilt on every drawer open
    // and only committed to the slots by the apply button.
    this.draft = new Map();
    this.draftSize = this.size;
    // Variants this visitor signed up for in this page view (back-in-stock). Display only —
    // Klaviyo holds the subscription, we just stop offering the same tile twice.
    this.notified = new Set();
    this.notifyTrigger = null;
    this.submitting = false;
    this.originalButtonLabels = new WeakMap();
    registry.set(this.formId, this);
    this.render();
  }

  disconnectedCallback() {
    if (registry.get(this.formId) === this) registry.delete(this.formId);
  }

  get form() {
    return document.forms[this.formId];
  }

  get drawerEl() {
    // By id on purpose: the drawer node is re-parented to <body> on open.
    return document.getElementById(this.drawerId);
  }

  get idInput() {
    return this.form?.elements['id'];
  }

  get isComplete() {
    return this.slots.length === this.size && this.slots.every((slot) => slot !== null);
  }

  /* Gift threshold reached? Display state only — an unbuyable gift still shows as unlocked. */
  get giftUnlocked() {
    return Boolean(this.gift.enabled) && this.size >= (this.gift.threshold || 0);
  }

  /* A gift that actually gets added to the cart (product mode with a buyable variant). */
  get giftInCart() {
    return this.giftUnlocked && Boolean(this.gift.variantId);
  }

  renderGift() {
    if (!this.giftEl) return;
    const unlocked = this.giftUnlocked;
    this.giftEl.classList.toggle('is-locked', !unlocked);
    const hint = this.giftEl.querySelector('[data-gift-hint]');
    if (hint) {
      const next = unlocked
        ? hint.getAttribute('data-hint-unlocked')
        : hint.getAttribute('data-hint-locked');
      // Only write on change so the aria-live region stays quiet on unrelated re-renders.
      if (next !== null && hint.textContent !== next) hint.textContent = next;
    }
  }

  /* Display name of a variant; prefixed with its size label in multi-size mode. */
  variantLabel(variant) {
    const source = this.multiSource ? this.sources[variant.source] : null;
    return source ? `${source.label} – ${variant.title}` : variant.title;
  }

  /*
   * Price HTML for the ATC button. Single-source: the Liquid-precomputed tier totals.
   * Multi-size: live sum of the picked variants, with the tier's preview percentage applied
   * using the same integer floor rounding as the Liquid tile prices.
   */
  setPriceHtml() {
    const tier = this.activeTier;
    // The Liquid-precomputed totals only exist for the tier sizes themselves. A set grown past
    // the biggest tier is summed live in both modes, with that tier's percentage applied.
    const exactTier = this.tiers[String(this.size)];
    if (!this.multiSource && exactTier) {
      return exactTier.discountedFormatted
        ? `<s>${exactTier.totalFormatted}</s> ${exactTier.discountedFormatted}`
        : exactTier.totalFormatted;
    }
    const total = this.slots.reduce((sum, id) => sum + (this.variants[id]?.price || 0), 0);
    const pct = tier?.pct || 0;
    if (pct > 0) {
      const discounted = Math.floor((total * (100 - pct)) / 100);
      return `<s>${formatMoney(total, this.pricing.moneyFormat)}</s> ${formatMoney(discounted, this.pricing.moneyFormat)}`;
    }
    return formatMoney(total, this.pricing.moneyFormat);
  }

  setSize(size) {
    this.size = size;
    const kept = this.slots.filter((slot) => slot !== null).slice(0, size);
    this.slots = kept.concat(new Array(size - kept.length).fill(null));
    this.render();
  }

  /* --- Drawer draft session ----------------------------------------------------------- */

  /* Rebuilds the draft from the committed slots — runs on every drawer open, so closing the
     drawer without applying simply discards whatever was clicked in that session. */
  startDraft() {
    this.draft = new Map();
    this.slots.forEach((id) => {
      if (id !== null) this.draft.set(id, (this.draft.get(id) || 0) + 1);
    });
    this.draftSize = this.size;
    this.hideDrawerHint();
    this.closeNotify();
    this.renderDrawer();
  }

  draftTotal() {
    let total = 0;
    this.draft.forEach((qty) => (total += qty));
    return total;
  }

  /* Smallest enabled tier above `size`, or null at the top (no automatic downgrade ever). */
  nextTierAbove(size) {
    return this.tierSizes.find((tierSize) => tierSize > size) ?? null;
  }

  /* Biggest enabled tier — the point from which the set grows freely instead of snapping. */
  get maxTier() {
    return this.tierSizes.length ? this.tierSizes[this.tierSizes.length - 1] : 1;
  }

  /*
   * Tier config for the current size. Above the biggest tier there is no own tier, so the
   * biggest one keeps applying: its percentage is what the admin's quantity discount grants
   * from that quantity upwards, and the ATC button needs a tier to show a price at all.
   */
  get activeTier() {
    return this.tiers[String(this.size)] || this.tiers[String(this.maxTier)] || null;
  }

  /* Stepper click: delta is +1 (plus / image tap) or -1. Handles the automatic tier upgrade
     when the pick exceeds the scratch size, and blocks with a hint at the biggest tier. */
  bumpDraft(variantId, delta) {
    const variant = this.variants[variantId];
    if (!variant) return;
    const current = this.draft.get(variantId) || 0;
    if (delta > 0) {
      if (!variant.available) return;
      // Inventory pre-check for duplicates (deny policy only) — /cart/add.js stays authoritative.
      if (variant.invManaged && variant.invPolicy === 'deny' && current + 1 > variant.invQty) {
        this.showDrawerHint(this.strings.stockLimit.replace('[color]', this.variantLabel(variant)));
        return;
      }
      if (this.draftTotal() + 1 > this.draftSize) {
        const next = this.nextTierAbove(this.draftSize);
        if (next === null) {
          // Past the biggest tier: no snapping to a set size any more, the pick simply grows
          // by one piece. Everything picked here lands in the cart as its own line item.
          this.draftSize = this.draftTotal() + 1;
          this.hideDrawerHint();
        } else {
          this.draftSize = next;
          this.showDrawerHint(this.strings.drawerUpgraded.replace('[count]', String(next)));
        }
      } else {
        this.hideDrawerHint();
      }
      this.draft.set(variantId, current + 1);
    } else {
      if (current <= 0) return;
      if (current === 1) this.draft.delete(variantId);
      else this.draft.set(variantId, current - 1);
      this.hideDrawerHint();
    }
    this.renderDrawer();
  }

  /* Commits the draft: tier radio + size, then the slots (incomplete is fine — the ATC button
     stays gated and reopens the drawer). Programmatic radio.checked fires no change event, so
     setSize() does not run a second time. */
  applyDraft() {
    const flat = [];
    this.draft.forEach((qty, id) => {
      for (let i = 0; i < qty; i++) flat.push(id);
    });
    // Above the biggest tier the set is exactly what was picked: there are no empty slots to
    // fill, so taking a piece back shrinks it instead of leaving a gap that would gate the
    // button with no tier left to complete. It never falls below the biggest tier that way —
    // that keeps the tiles' "never downgrade automatically" rule intact.
    const size = this.draftSize > this.maxTier ? Math.max(flat.length, this.maxTier) : this.draftSize;
    if (size !== this.size) {
      // Above the biggest tier no radio matches — the biggest tile stays the checked one, so
      // the tiles keep showing which incentive is reached while the set itself is larger.
      const radio = this.querySelector(`[data-set-size][value="${Math.min(size, this.maxTier)}"]`);
      if (radio) radio.checked = true;
      this.size = size;
    }
    this.slots = flat.slice(0, this.size);
    while (this.slots.length < this.size) this.slots.push(null);
    this.render();
  }

  /* Syncs the drawer to the draft: tile counts + selection rings, footer thumbnails (filled
     pieces first, then empty placeholders up to the scratch size), running count. */
  renderDrawer() {
    const drawer = this.drawerEl;
    if (!drawer) return;
    drawer.querySelectorAll('.set-builder__color-tile').forEach((tile) => {
      const variantId = tile.getAttribute('data-variant-id');
      const qty = this.draft.get(variantId) || 0;
      const qtyEl = tile.querySelector('[data-tile-qty]');
      if (qtyEl) qtyEl.textContent = String(qty);
      tile.classList.toggle('is-selected', qty > 0);
      const minus = tile.querySelector('[data-tile-minus]');
      if (minus) minus.disabled = qty === 0;
      // Sold-out tile the visitor already signed up for: confirm in place instead of
      // offering the same signup again.
      const notifyLabel = tile.querySelector('[data-notify-label]');
      if (notifyLabel) {
        const done = this.notified.has(variantId);
        tile.classList.toggle('is-notified', done);
        notifyLabel.textContent = done ? this.strings.notifyDone : this.strings.notifyButton;
      }
    });
    const thumbsEl = drawer.querySelector('[data-sb-thumbs]');
    let filled = 0;
    if (thumbsEl) {
      let html = '';
      this.draft.forEach((qty, id) => {
        const variant = this.variants[id];
        for (let i = 0; i < qty; i++) {
          html += variant?.image
            ? `<span class="set-builder__drawer-thumb"><img src="${variant.image}" alt="" loading="lazy" width="48" height="48"></span>`
            : '<span class="set-builder__drawer-thumb"></span>';
          filled++;
        }
      });
      for (let i = filled; i < this.draftSize; i++) {
        html += '<span class="set-builder__drawer-thumb set-builder__drawer-thumb--empty"></span>';
      }
      thumbsEl.innerHTML = html;
    } else {
      filled = this.draftTotal();
    }
    const countEl = drawer.querySelector('[data-sb-count]');
    if (countEl) {
      countEl.textContent = this.strings.drawerCount
        .replace('[current]', String(filled))
        .replace('[total]', String(this.draftSize));
    }
  }

  /* --- Back-in-stock sheet -------------------------------------------------------------- */

  /*
   * Sold-out tiles open a notify sheet in the drawer footer, taking the selection bar's place
   * (no second overlay). The shared <back-in-stock-form> is retargeted per tile: it opts out
   * of the theme's global variant:change sync, so writing variant-id here is authoritative.
   */
  openNotify(variantId, trigger) {
    const drawer = this.drawerEl;
    const sheet = drawer?.querySelector('[data-sb-notify]');
    if (!sheet) return;
    const variant = this.variants[variantId];
    const form = sheet.querySelector('back-in-stock-form');
    if (form) form.setAttribute('variant-id', String(variantId));

    const title = sheet.querySelector('[data-sb-notify-title]');
    if (title && variant) title.textContent = this.variantLabel(variant);

    const thumb = sheet.querySelector('[data-sb-notify-thumb]');
    if (thumb) {
      thumb.innerHTML = variant?.image
        ? `<img src="${variant.image}" alt="" loading="lazy" width="48" height="48">`
        : '';
    }

    // Clear a message left over from a previous tile before showing the sheet again.
    const message = sheet.querySelector('.back-in-stock__message');
    if (message) {
      message.textContent = '';
      message.hidden = true;
    }

    this.notifyTrigger = trigger || null;
    drawer.querySelector('[data-sb-selection]')?.setAttribute('hidden', '');
    sheet.hidden = false;
    sheet.querySelector('input[name="email"]')?.focus();
  }

  closeNotify() {
    const drawer = this.drawerEl;
    const sheet = drawer?.querySelector('[data-sb-notify]');
    if (!sheet || sheet.hidden) return false;
    sheet.hidden = true;
    drawer.querySelector('[data-sb-selection]')?.removeAttribute('hidden');
    // Hand focus back to the tile that opened the sheet — it is still the relevant control.
    this.notifyTrigger?.focus();
    this.notifyTrigger = null;
    return true;
  }

  get notifyOpen() {
    const sheet = this.drawerEl?.querySelector('[data-sb-notify]');
    return Boolean(sheet) && !sheet.hidden;
  }

  showDrawerHint(message) {
    const hint = this.drawerEl?.querySelector('[data-sb-drawer-hint]');
    if (!hint) return;
    hint.textContent = message;
    hint.hidden = false;
  }

  hideDrawerHint() {
    const hint = this.drawerEl?.querySelector('[data-sb-drawer-hint]');
    if (hint) hint.hidden = true;
  }

  /* --- Committed state ------------------------------------------------------------------ */

  clearSlot(index) {
    if (this.size > this.maxTier) {
      // Extra pieces beyond the biggest tier are free-form: removing one shrinks the set
      // rather than leaving a hole that would gate the ATC button with no tier to fill.
      this.slots.splice(index, 1);
      this.size = this.slots.length;
      this.render();
      return;
    }
    this.slots[index] = null;
    this.render();
  }

  reset() {
    this.slots = new Array(this.size).fill(null);
    this.render();
  }

  /*
   * snippets/set-builder.liquid renders one slot tile per piece of the biggest tier. A set
   * grown past it needs more tiles, so the first one is cloned. All slot interactions run
   * through document-level delegation, so a clone is wired up by simply existing.
   */
  ensureSlotCapacity(count) {
    const template = this.slotEls[0];
    const container = template?.parentElement;
    if (!container) return;
    while (this.slotEls.length < count) {
      const clone = template.cloneNode(true);
      clone.classList.remove('is-filled');
      container.appendChild(clone);
      this.slotEls.push(clone);
    }
  }

  render() {
    this.ensureSlotCapacity(this.size);
    this.slotEls.forEach((slotEl, index) => {
      slotEl.hidden = index >= this.size;
      if (index >= this.size) return;
      const button = slotEl.querySelector('[data-slot-open]');
      const media = slotEl.querySelector('.set-builder__slot-media');
      const label = slotEl.querySelector('.set-builder__slot-label');
      const remove = slotEl.querySelector('[data-slot-remove]');
      const variant = this.slots[index] ? this.variants[this.slots[index]] : null;
      const slotName = this.strings.slotLabel.replace('[index]', String(index + 1));
      if (variant) {
        const name = this.variantLabel(variant);
        media.innerHTML = variant.image
          ? `<img src="${variant.image}" alt="" loading="lazy" width="90" height="90">`
          : '';
        label.textContent = name;
        slotEl.classList.add('is-filled');
        button.setAttribute('aria-label', `${slotName}: ${name}`);
        remove.hidden = false;
        remove.setAttribute('aria-label', this.strings.removeColor.replace('[color]', name));
      } else {
        media.innerHTML =
          '<svg role="presentation" focusable="false" width="16" height="16" viewBox="0 0 16 16" class="set-builder__slot-plus"><path d="M7 1h2v6h6v2H9v6H7V9H1V7h6V1z" fill="currentColor"/></svg>';
        label.textContent = this.strings.slotEmpty;
        slotEl.classList.remove('is-filled');
        button.setAttribute('aria-label', `${slotName}: ${this.strings.slotEmpty}`);
        remove.hidden = true;
      }
    });
    this.renderGift();
    this.updateAtcUi();
  }

  updateAtcUi() {
    const form = this.form;
    if (!form) return;
    // Dynamic checkout (payment button) buys the hidden id input directly and cannot express a
    // multi-item set — hide it via CSS while a set tier is active.
    form.classList.toggle('set-builder-set-mode', this.size > 1 || this.giftInCart);
    const buttons = Array.from(form.elements).filter((el) => el.type === 'submit');
    if (this.size === 1 && !this.giftInCart) {
      // Single mode: native ProductForm flow; keep the theme's own button wording — except in
      // multi-size mode, where the picked size determines the price and the theme's static
      // wording (main product price elsewhere on the page) would be misleading.
      if (this.idInput) this.idInput.disabled = false;
      if (this.isComplete && this.idInput) this.idInput.value = this.slots[0];
      buttons.forEach((button) => {
        if (this.multiSource && this.isComplete) {
          if (!this.originalButtonLabels.has(button)) this.originalButtonLabels.set(button, button.innerHTML);
          button.innerHTML = `${this.strings.addToCart} &nbsp;·&nbsp; ${this.setPriceHtml()}`;
        } else {
          const original = this.originalButtonLabels.get(button);
          if (original !== undefined) button.innerHTML = original;
        }
        button.toggleAttribute('aria-disabled', !this.isComplete);
        button.classList.toggle('set-builder-incomplete', !this.isComplete);
      });
      return;
    }
    // Set mode: neutralize the hidden id input so no stray native submit can add a single item.
    if (this.idInput) this.idInput.disabled = true;
    const tier = this.activeTier;
    buttons.forEach((button) => {
      if (!this.originalButtonLabels.has(button)) this.originalButtonLabels.set(button, button.innerHTML);
      if (this.isComplete && tier) {
        button.innerHTML = `${this.strings.addToCart} &nbsp;·&nbsp; ${this.setPriceHtml()}`;
        button.removeAttribute('aria-disabled');
        button.classList.remove('set-builder-incomplete');
      } else {
        button.innerHTML = this.strings.chooseColors;
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('set-builder-incomplete');
      }
    });
  }

}

/*
 * Minimal Shopify money formatter for the live multi-size total, driven by shop.money_format
 * from the block config. Only the placeholder styles Shopify offers are supported; unknown
 * placeholders fall back to the plain "amount" style.
 */
function formatMoney(cents, format) {
  const [units, decimals] = (cents / 100).toFixed(2).split('.');
  const grouped = (separator) => units.replace(/\B(?=(\d{3})+(?!\d))/g, separator);
  const styles = {
    amount: `${grouped(',')}.${decimals}`,
    amount_no_decimals: grouped(','),
    amount_with_comma_separator: `${grouped('.')},${decimals}`,
    amount_no_decimals_with_comma_separator: grouped('.'),
    amount_with_apostrophe_separator: `${grouped("'")}.${decimals}`,
    amount_no_decimals_with_space_separator: grouped(' '),
    amount_with_space_separator: `${grouped(' ')},${decimals}`,
    amount_with_period_and_space_separator: `${grouped(' ')}.${decimals}`,
  };
  return (format || '{{ amount }}').replace(/\{\{\s*(\w+)\s*\}\}/, (match, style) => styles[style] || styles.amount);
}

/* --- Cart contract ------------------------------------------------------------------------ */

/*
 * The single swap point for a different backend (e.g. a bundle app): map the builder state to
 * /cart/add.js items. "single_lines" aggregates duplicate colors into one item with quantity.
 */
function buildAddPayload(builder) {
  if (CONTRACT === 'single_lines') {
    const counts = new Map();
    builder.slots.forEach((id) => counts.set(id, (counts.get(id) || 0) + 1));
    const items = Array.from(counts, ([id, quantity]) => ({ id: Number(id), quantity }));
    if (builder.giftInCart) {
      items.push({
        id: Number(builder.gift.variantId),
        quantity: 1,
        properties: { [GIFT_PROPERTY_KEY]: '1' },
      });
    }
    return items;
  }
  return [];
}

/* Replicates ProductForm._onSubmit (assets/theme.js) with a JSON multi-item body. */
async function addSetToCart(builder) {
  if (builder.submitting || !builder.form) return;
  builder.submitting = true;
  const buttons = Array.from(builder.form.elements).filter((el) => el.type === 'submit');
  buttons.forEach((button) => button.setAttribute('aria-busy', 'true'));

  const sections = ['variant-added'];
  document.documentElement.dispatchEvent(
    new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } })
  );

  const items = buildAddPayload(builder);
  const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Requested-With': 'XMLHttpRequest', // needed for Shopify to check inventory
    },
    body: JSON.stringify({
      items,
      sections: sections.join(','),
      sections_url: `${Shopify.routes.root}variants/${items[0]?.id}`,
    }),
  });

  buttons.forEach((button) => button.removeAttribute('aria-busy'));
  builder.submitting = false;
  const responseJson = await response.json();

  if (response.ok) {
    if (
      window.themeVariables.settings.cartType === 'page' ||
      window.themeVariables.settings.pageType === 'cart'
    ) {
      window.location.href = `${Shopify.routes.root}cart`;
      return;
    }
    const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
    cartContent['sections'] = responseJson['sections'];
    builder.form.dispatchEvent(
      new CustomEvent('variant:add', {
        bubbles: true,
        detail: {
          items: responseJson.hasOwnProperty('items') ? responseJson['items'] : [responseJson],
          cart: cartContent,
        },
      })
    );
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'variant:add', cart: cartContent },
      })
    );
    builder.reset();
  } else {
    builder.form.dispatchEvent(
      new CustomEvent('cart:error', {
        bubbles: true,
        detail: { error: responseJson['description'] },
      })
    );
    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }
}

/* --- Document-level wiring (once-guard: survives theme-editor re-renders) ------------------ */

function builderFor(element) {
  const host = element.closest('set-builder');
  if (host) return host;
  const drawer = element.closest('[data-set-builder-drawer]');
  if (drawer) return document.getElementById(drawer.getAttribute('data-set-builder-for'));
  return null;
}

if (!window.__setBuilderBound) {
  window.__setBuilderBound = true;

  // Step 1: set size change
  document.addEventListener('change', (event) => {
    const radio = event.target.closest('[data-set-size]');
    if (!radio) return;
    builderFor(radio)?.setSize(parseInt(radio.value, 10));
  });

  document.addEventListener('click', (event) => {
    // Tier tile: a radio that is already checked fires no change event. Once the set has grown
    // past the biggest tier, that checked tile no longer matches the actual size — clicking it
    // is the way back down, so it is handled here instead of only in the change listener.
    const sizeRadio = event.target.closest('[data-set-size]');
    if (sizeRadio) {
      const builder = builderFor(sizeRadio);
      const value = parseInt(sizeRadio.value, 10);
      if (builder && builder.size !== value) builder.setSize(value);
      return;
    }

    // Slot × on the PDP: clears that slot. (The slot button itself opens the drawer
    // declaratively through the theme's aria-controls delegation — nothing to do here.)
    const slotRemove = event.target.closest('[data-slot-remove]');
    if (slotRemove) {
      const builder = builderFor(slotRemove);
      if (builder) builder.clearSlot(builder.slotEls.indexOf(slotRemove.closest('[data-slot]')));
      return;
    }

    // Stepper inside the drawer: plus / minus buttons, and the tile image counts as +1.
    const stepper = event.target.closest('[data-tile-add], [data-tile-plus], [data-tile-minus]');
    if (stepper && !stepper.disabled) {
      const tile = stepper.closest('.set-builder__color-tile');
      const builder = builderFor(stepper);
      if (tile && builder) {
        builder.bumpDraft(tile.getAttribute('data-variant-id'), stepper.matches('[data-tile-minus]') ? -1 : 1);
      }
      return;
    }

    // Sold-out tile: image, bell and button all open the notify sheet for that variant.
    const notify = event.target.closest('[data-tile-notify]');
    if (notify) {
      const tile = notify.closest('.set-builder__color-tile');
      const builder = builderFor(notify);
      if (tile && builder) builder.openNotify(tile.getAttribute('data-variant-id'), notify);
      return;
    }

    if (event.target.closest('[data-sb-notify-close]')) {
      builderFor(event.target)?.closeNotify();
      return;
    }

    // Apply: commit the draft to the slots and close the drawer.
    const apply = event.target.closest('[data-sb-apply]');
    if (apply) {
      const builder = builderFor(apply);
      if (builder) {
        builder.applyDraft();
        builder.drawerEl?.hide();
      }
    }
  });

  // Escape with the notify sheet open closes the sheet, not the whole drawer. Capture phase on
  // document, registered at load, so this runs before the drawer's focus trap (which listens on
  // document too and deactivates — closing the drawer — on Escape). stopImmediatePropagation is
  // required, not stopPropagation: same-node listeners keep running after the latter.
  document.addEventListener(
    'keydown',
    (event) => {
      if (event.key !== 'Escape') return;
      // Scan the registry rather than the event target: focus may sit anywhere in the drawer
      // (or on the drawer itself via the trap's fallback) when Escape arrives.
      const builder = Array.from(registry.values()).find((instance) => instance.notifyOpen);
      if (!builder) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      builder.closeNotify();
    },
    true
  );

  // Successful Klaviyo signup: remember the variant so its tile confirms instead of re-offering.
  // The sheet stays open with its success message — closing it reveals the confirmed tile.
  document.addEventListener('back-in-stock:subscribed', (event) => {
    const builder = builderFor(event.target);
    const variantId = event.detail?.variantId;
    if (!builder || !variantId) return;
    builder.notified.add(String(variantId));
    builder.renderDrawer();
  });

  // ATC interception (capture phase — runs before ProductForm's submit can be initiated).
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('button[type="submit"]');
      if (!button || !button.form) return;
      const builder = registry.get(button.form.getAttribute('id'));
      if (!builder) return;
      if (!builder.isComplete) {
        // Incomplete set: the button opens the color drawer instead of submitting — picking
        // the missing pieces there is the only way forward, so lead the shopper straight in.
        event.preventDefault();
        event.stopPropagation();
        builder.drawerEl?.show();
        return;
      }
      if (builder.size === 1 && !builder.giftInCart) {
        // Native flow: ensure the hidden id input carries the picked variant.
        if (builder.idInput) {
          builder.idInput.disabled = false;
          builder.idInput.value = builder.slots[0];
        }
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      addSetToCart(builder);
    },
    true
  );

  // Drawer open: lazy-stamp the color grid on the first open (mobile-menu pattern), then
  // start a fresh draft session from the committed slots.
  document.addEventListener('dialog:after-show', (event) => {
    const drawer = event.target;
    if (!drawer.matches?.('[data-set-builder-drawer]')) return;
    const template = drawer.querySelector('template[data-set-builder-lazy]');
    if (template) template.replaceWith(template.content);
    document.getElementById(drawer.getAttribute('data-set-builder-for'))?.startDraft();
  });
}

if (!window.customElements.get('set-builder')) {
  window.customElements.define('set-builder', SetBuilder);
}
